import React, { useState } from "react";
import Button from "../../../../components/form/Button";
import TicketsOrderItem from "../../../../components/sections/front/user-tickets/TicketsOrderItem";
import {
  useQuery,
  keepPreviousData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { orderList } from "../../../../services/orderService";
import { useDebounce } from "../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "../../../../components/form/select/Select";
import Image from "../../../../components/elements/Image";
import Skeleton from "../../../../components/loader/Skeleton";
import TicketOrderLoader from "../../../../components/loader/TicketOrderLoader";
import NoData from "../../../../components/elements/Nodata";
import noDataImage from "../../../../assets/images/tickets.svg";
import { chatCreate } from "../../../../services/chatService";
import { replaceOldData } from "../../../../helpers";
import { t } from "i18next";

const TicketsOrderList = () => {
  const navigate = useNavigate();
  const { session } = useSelector((state) => state?.authProvider);
  const queryClient = useQueryClient();
  const { lang } = useSelector((state) => state?.langProvider);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: orderData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["order-buyer", filters, search],
    queryFn: async () => {
      const res = await orderList({
        ...filters,
        ...(search && { keyword: search }),
        viewAs: "buyer",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      const res_data = res?.docs?.data?.map((item) => {
        return {
          ...item,
          id: item?._id,
          link: `/tickets-order?orderId=${item?.uid}`,
          month: dayjs(item?.createdAt).format("MMM"),
          date: dayjs(item?.createdAt).format("DD"),
          image: item?.eventData?.gallery?.[0]?.url,
          title: lngDetect(item?.eventData?.title),
          // time: `${dayjs(item?.eventData?.startDate).format(
          //   "DD MMM, YYYY hh:mm A"
          // )} - ${dayjs(item?.eventData?.endDate).format(
          //   "DD MMM, YYYY hh:mm A"
          // )}`,
          startDate: item?.eventData?.startDate,
          endDate: item?.eventData?.endDate,
          orderinfo: `${item?.orderId} of (US$${parseFloat(item?.total).toFixed(
            2
          )}) purchased on ${dayjs(item?.createdAt).format(
            "ddd, MMM DD, hh:mm A"
          )}`,
        };
      });

      if (filters?.page > 1) {
        const marge_arr = [...(orderData?.data || []), ...res_data];
        return {
          ...res?.docs,
          data: replaceOldData({
            oldData: marge_arr,
            newData: res_data,
          }),
        };
      } else {
        return {
          ...res?.docs,
          data: res_data,
        };
      }
    },
    placeholderData: keepPreviousData,
  });

  return (
    <div className="rounded-xl shadow-sm bg-white py-4 px-4 md:px-8 mx-auto">
      <div className="flex flex-col gap-6">
        <div className="w-36 ml-auto">
          <Select
            icon="fa-light fa-filter"
            placeholder={t("Filter By")}
            selectedValue={filters?.status}
            dropdownData={[
              { name: t("All"), value: "all" },
              { name: t("Processing"), value: "processing" },
              { name: t("Canceled"), value: "canceled" },
              { name: t("Confirmed"), value: "confirmed" },
              // { name: "Shipped", value: "shipped" },
              // { name: "Pickup", value: "pickup" },
              // { name: "Return Requested", value: "return-request" },
              // { name: "Rejected", value: "rejected" },
            ]}
            onChange={(val) => {
              if (val === "all") {
                setFilters((pre) => {
                  delete pre?.status;
                  return { ...pre, page: 1 };
                });
                queryClient.invalidateQueries({
                  queryKey: ["order-buyer"],
                  exact: false,
                });
              } else {
                setFilters((pre) => ({ ...pre, page: 1, status: val }));
              }
            }}
          />
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 2xl:gap-8">
          {isLoading ||
          (isFetching && filters?.status && filters?.page === 1) ? (
            Array.from({ length: 5 }, (_, i) => <TicketOrderLoader key={i} />)
          ) : orderData?.data?.length > 0 ? (
            orderData?.data?.map((ticket) => (
              <TicketsOrderItem key={ticket.id} data={ticket} />
            ))
          ) : (
            <div className="col-span-2 bg-white w-full pb-6 px-8 rounded-lg flex flex-col items-center justify-center">
              <NoData
                image={noDataImage}
                message={t("No orders found")}
                imageClass="w-80 h-80"
              />
            </div>
          )}
        </div>
      </div>
      {orderData?.metadata?.hasNextPage && (
        <div className="flex items-center justify-center my-10">
          <Button
            buttonLabel={t("see past orders")}
            buttonFunction={() => {
              if (orderData?.metadata?.hasNextPage) {
                setFilters((pre) => ({
                  ...pre,
                  page: orderData?.metadata?.nextPage,
                }));
              }
            }}
            {...((isLoading || isFetching) && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      )}
    </div>
  );
};

export default TicketsOrderList;
